import { useRef, forwardRef, useImperativeHandle } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
  Control,
  UseFormWatch,
  UseFormSetValue,
  UseFormGetValues,
} from "react-hook-form";
import { notification } from "antd";
import { useCreatePaymentMethodsMutation } from "@/src/store/apis/subscription";
import { useAppSelector } from "@/src/store";
import { isFormValid as isFormValidUtil, getCardNumber } from "./utils";
import FormItem from "@/src/components/molecule/FormItem";
import SectionCardRow from "@/src/components/molecule/SectionCardRow";
import Input from "@/src/components/atom/Input";
import Label from "@/src/components/atom/Label";
import { ReactComponent as DotGray7Svg } from "@/src/assets/icons/icon-dot-gray7.svg";
import {
  CARD_REGISTER_TYPE_OPTION_LIST,
  REGISTER_TYPE_OPTION_LIST,
} from "@/src/constant/optionList";
import {
  isValidBirthDay,
  isValidExpirationDate,
} from "@/src/pages/exporter/SubscriptionManagement/utils/isValidExpirationDate";
import colorSet from "@/src/styles/color";
import { CARD_NUMBER_CHECK_LENGTH, CARD_SEGMENT_LENGTHS } from "./constants";

export type RegisterType = "BUSINESS" | "PERSONAL";
export type CardType = "CORPORATE_CARD" | "EMPLOYEE_PERSONALIZED_CARD";

export interface PaymentMethodFormRef {
  submitPaymentMethod: () => Promise<void>;
  isLoading: boolean;
}

export interface PaymentFormValues {
  registerType: RegisterType;
  cardType: CardType;
  cardNumber: string[];  // Add cardNumber to form values
  password2: string;
  expiredDate: string;
  userInfo: string;
}

interface PaymentMethodFormProps {
  control: Control<PaymentFormValues>;
  watch: UseFormWatch<PaymentFormValues>;
  setValue: UseFormSetValue<PaymentFormValues>;
  getValues: UseFormGetValues<PaymentFormValues>;
  businessNumber?: string;
  onSubmit?: (paymentMethodId: number) => void;
  onError?: (error: string) => void;
}

const PaymentMethodForm = forwardRef<PaymentMethodFormRef, PaymentMethodFormProps>(({
  control,
  watch,
  setValue,
  getValues,
  businessNumber = "",
  onSubmit,
  onError,
}, ref) => {
  const { t } = useTranslation();
  // Use refs array for better performance instead of DOM traversal
  const inputRefs = useRef<(HTMLInputElement | null)[]>([null, null, null, null]);
  // Track if arrow key was just pressed to prevent focus change
  const arrowKeyPressed = useRef<boolean>(false);
  
  // API hooks
  const [createPaymentMethod, { isLoading: isCreating }] = useCreatePaymentMethodsMutation();
  const { user } = useAppSelector(state => state.auth);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    submitPaymentMethod: handleSubmitPaymentMethod,
    isLoading: isCreating,
  }));

  // Watch cardNumber from form state
  const cardNumber = watch('cardNumber') || ['', '', '', ''];

  // Handle payment method submission
  const handleSubmitPaymentMethod = async () => {
    try {
      if (!user?.exporter?.id) {
        const errorMsg = t('error:userNotFound');
        onError?.(errorMsg);
        notification.error({ message: errorMsg });
        return;
      }

      const formValues = getValues();
      const fullCardNumber = formValues.cardNumber.join('');
      const expiredDateFormatted = formValues.expiredDate.replace('/', '');
      
      // Determine userInfo based on registerType
      let userInfoToSend: string | null = null;
      if (formValues.registerType === 'BUSINESS') {
        if (formValues.cardType === 'EMPLOYEE_PERSONALIZED_CARD') {
          userInfoToSend = formValues.userInfo;
        } else {
          userInfoToSend = businessNumber;
        }
      } else {
        userInfoToSend = formValues.userInfo;
      }

      const createPaymentData = {
        cardNumber: fullCardNumber,
        expiryDate: expiredDateFormatted,
        password2: formValues.password2,
        userInfo: userInfoToSend,
        exporterId: user.exporter.id,
        isRepresentative: true, // First card is representative by default
      };

      const result = await createPaymentMethod(createPaymentData).unwrap();
      
      onSubmit?.(result.row.id);
    } catch (error: any) {
      const errorMsg = error?.data?.message || t('error:paymentMethodRegistrationFailed');
      onError?.(errorMsg);
      notification.error({ message: errorMsg });
    }
  };

  // Handle card segment change
  const handleCardSegmentChange = (value: string, idx: number) => {
    // Don't process if arrow key was just pressed
    if (arrowKeyPressed.current) {
      return;
    }

    // Check if this was triggered by arrow key (value unchanged)
    const currentValue = cardNumber[idx];
    if (value === currentValue) {
      // Value unchanged, likely from arrow key - don't process
      return;
    }

    const cleanValue = value.replace(/\D/g, '');
    const maxLength = CARD_SEGMENT_LENGTHS[idx]; // Use segment-specific length
    const newValue = cleanValue.slice(0, maxLength);

    // Get previous value to check if we're editing or newly entering
    const previousValue = cardNumber[idx];
    const wasAlreadyFull = previousValue.length === maxLength;

    const updatedCardNumber = [...cardNumber];
    updatedCardNumber[idx] = newValue;
    setValue('cardNumber', updatedCardNumber);

    // Auto-focus next input only when newly reaching max length (not when editing)
    if (newValue.length === maxLength && idx < 3 && !wasAlreadyFull && !arrowKeyPressed.current) {
      const nextInput = inputRefs.current[idx + 1];
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  const handleCardNumberInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    idx: number,
  ) => {
    const isBackSpaceKeyDown = e.code === "Backspace";
    const isArrowKey = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key);
    const isTabKey = e.key === "Tab";
    const currentFieldLength = cardNumber[idx].length;
    const maxFieldLength = CARD_SEGMENT_LENGTHS[idx];
    const input = e.currentTarget;
    const cursorPosition = input.selectionStart || 0;

    // Handle arrow keys - completely prevent focus change
    if (isArrowKey) {
      // Mark that arrow key was pressed
      arrowKeyPressed.current = true;

      const isRightArrow = e.key === "ArrowRight";
      const isLeftArrow = e.key === "ArrowLeft";

      // Check if cursor is at the edge of the field
      const atEnd = cursorPosition === currentFieldLength;
      const atStart = cursorPosition === 0;

      // If field is full, prevent any automatic focus movement
      if (currentFieldLength === maxFieldLength) {
        // For right arrow at the end of field, prevent moving to next field
        if (isRightArrow && atEnd) {
          e.preventDefault();
          e.stopPropagation();
          // Keep focus on current input
          setTimeout(() => {
            input.focus();
            input.setSelectionRange(currentFieldLength, currentFieldLength);
          }, 0);
          return;
        }

        // For left arrow at the start of field, prevent moving to previous field
        if (isLeftArrow && atStart) {
          e.preventDefault();
          e.stopPropagation();
          // Keep focus on current input
          setTimeout(() => {
            input.focus();
            input.setSelectionRange(0, 0);
          }, 0);
          return;
        }
      }

      // Allow normal cursor movement within the field
      return;
    }

    // Handle Tab key - allow natural tab navigation
    if (isTabKey) {
      // Allow default tab behavior for navigation between fields
      return;
    }

    // Handle backspace for empty field - move to previous field
    if (currentFieldLength === 0 && isBackSpaceKeyDown && idx > 0) {
      e.preventDefault();
      const prevInput = inputRefs.current[idx - 1];
      if (prevInput) {
        prevInput.focus();
        // Set cursor to end of previous field
        setTimeout(() => {
          const prevLength = cardNumber[idx - 1].length;
          prevInput.setSelectionRange(prevLength, prevLength);
        }, 0);
      }
    }
  };

  const handleExpiredDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value.replace(/[^0-9]/g, "");

    if (inputValue.length > 4) {
      inputValue = inputValue.slice(0, 4);
    }
    if (inputValue.length > 2) {
      inputValue = `${inputValue.slice(0, 2)}/${inputValue.slice(2)}`;
    }

    setValue("expiredDate", inputValue);
  };

  // UserInfo FormItem 컴포넌트 - 중복 제거를 위해 추출
  const UserInfoFormItem = ({ isRequired = true }: { isRequired?: boolean }) => {
    return (
      <FormItem<PaymentFormValues, "userInfo">
        label={t("subscriptionPlanSignup:userInfo")}
        type="text"
        name="userInfo"
        control={control}
        rules={{
          required: isRequired,
          minLength: 6,
          validate: (value) => {
            return isValidBirthDay(value);
          },
        }}
        errorsMessage={{
          required: t("error:required"),
          minLength: t("error:enterDateYYMMDD"),
          validate: t("error:enterDateYYMMDD"),
        }}
        direction="vertical"
        inputProps={{
          placeholder: t("subscriptionPlanSignup:userInfoPlaceholder"),
          maxLength: 6,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            setValue("userInfo", e.target.value.replace(/[^0-9]/g, ""));
          },
        }}
      />
    );
  };

  const renderRegisterTypeItem = () => {
    if (watch("registerType") === "BUSINESS") {
      return (
        <>
          <FormItem<PaymentFormValues, "cardType">
            label={t("common:cardType")}
            type="radioGroup"
            name="cardType"
            control={control}
            rules={{ required: true }}
            direction="vertical"
            options={CARD_REGISTER_TYPE_OPTION_LIST.map((item) => {
              return { ...item, label: t(item.langKey) };
            })}
            inputProps={{
              onChange: (e) => {
                setValue("cardType", e.target.value);
              },
            }}
          />
          {watch("cardType") === "EMPLOYEE_PERSONALIZED_CARD" && (
            <UserInfoFormItem isRequired={true} />
          )}
          <SectionCardRow
            label={
              <Label required>
                {t("subscriptionPlanSignup:businessNumber")}
              </Label>
            }
            value={<Input disabled value={businessNumber} allowClear={false} />}
            direction="vertical"
          />
        </>
      );
    }

    if (watch("registerType") === "PERSONAL") {
      return <UserInfoFormItem isRequired={true} />;
    }
  };

  return (
    <FormContainer>
      {/* STEP 1: 법인/개인 선택 라디오 */}
      <FormItem<PaymentFormValues, "registerType">
        label={t("subscriptionPlanSignup:selectBusinessIndividual")}
        type="radioGroup"
        name="registerType"
        control={control}
        rules={{ required: true }}
        direction="vertical"
        options={REGISTER_TYPE_OPTION_LIST.map((item) => {
          return { ...item, label: t(item.langKey) };
        })}
        inputProps={{
          onChange: (e) => {
            setValue("registerType", e.target.value);
            setValue("cardType", "CORPORATE_CARD");
            setValue("userInfo", "");
          },
        }}
      />
      {renderRegisterTypeItem()}
      <Divider />

      {/* STEP 2: 카드 번호 입력 */}
      <SectionCardRow
        direction="vertical"
        label={<Label required>{t("subscriptionPlanSignup:cardNumber")}</Label>}
        value={
          <FlexRow $gap={8}>
            {cardNumber.map((segment, idx) => {
              return (
                <Input
                  key={idx.toString()}
                  ref={(el) => (inputRefs.current[idx] = el)}
                  type="text"
                  value={segment}
                  placeholder={t("placeholder:enter")}
                  maxLength={CARD_SEGMENT_LENGTHS[idx]}
                  allowClear={false}
                  autoComplete="off"
                  onChange={(e) => handleCardSegmentChange(e.target.value, idx)}
                  onKeyDown={(e) => {
                    handleCardNumberInputKeyDown(e, idx);
                  }}
                  onKeyUp={(e) => {
                    // Also prevent on keyup
                    const isArrowKey = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key);
                    if (isArrowKey) {
                      arrowKeyPressed.current = false;
                      e.preventDefault();
                      e.stopPropagation();
                      // Use nativeEvent for stopImmediatePropagation if available
                      if (e.nativeEvent.stopImmediatePropagation) {
                        e.nativeEvent.stopImmediatePropagation();
                      }
                    }
                  }}
                  onInput={(e) => {
                    // Prevent any input event from arrow keys
                    const input = e.currentTarget as HTMLInputElement;
                    if (input.value === segment) {
                      // Value unchanged, likely from arrow key
                      e.stopPropagation();
                    }
                  }}
                />
              );
            })}
          </FlexRow>
        }
      />

      {/* STEP 3: 만료일 & 비밀번호 */}
      <FlexRow $gap={8}>
        <StyledFormItem<PaymentFormValues, "expiredDate">
          label={t("subscriptionPlanSignup:expiredDate")}
          type="text"
          name="expiredDate"
          control={control}
          rules={{
            required: true,
            minLength: 5,
            validate: (value) => {
              return isValidExpirationDate(value.replace(/[^0-9]/g, ""));
            },
          }}
          errorsMessage={{
            required: t("error:required"),
            validate: t("error:confirmDateMMYY"),
            minLength: t("error:confirmDateMMYY"),
          }}
          direction="vertical"
          inputProps={{
            placeholder: t("subscriptionPlanSignup:expiredDatePlaceholder"),
            maxLength: 5,
            autoComplete: "off",
            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
              handleExpiredDateChange(e),
            onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === "/") {
                e.preventDefault();
              }
            },
          }}
        />
        <StyledSectionCardRow
          label={<Label required>{t("subscriptionPlanSignup:password")}</Label>}
          direction="vertical"
          value={
            <Grid>
              <StyledPasswordFormItem<PaymentFormValues, "password2">
                type="text"
                name="password2"
                control={control}
                rules={{ required: true }}
                errorsMessage={{
                  required: t("error:required"),
                }}
                direction="vertical"
                inputProps={{
                  type: "password",
                  placeholder: t("subscriptionPlanSignup:passwordPlaceholder"),
                  maxLength: 2,
                  autoComplete: "new-password",
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    setValue(
                      "password2",
                      e.target.value.replace(/[^0-9]/g, ""),
                    );
                  },
                }}
              />
              <DotWrapper>
                <Dot />
              </DotWrapper>
              <DotWrapper>
                <Dot />
              </DotWrapper>
            </Grid>
          }
        />
      </FlexRow>
    </FormContainer>
  );
});

PaymentMethodForm.displayName = 'PaymentMethodForm';

export default PaymentMethodForm;

// Styled Components
const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Divider = styled.div`
  width: 100%;
  border-bottom: 1px solid ${colorSet.gray9};
`;

const FlexColumn = styled.div<{ $gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap ?? 4}px;
  width: 100%;
`;

const FlexRow = styled(FlexColumn)`
  flex-direction: row;
  align-items: center;
`;

const StyledFormItem = styled(FormItem)`
  flex: 1;
  align-self: baseline;
`;

const Dot = styled(DotGray7Svg)`
  width: 8px;
  height: 8px;

  * {
    fill: ${colorSet.gray2};
  }
`;

const StyledSectionCardRow = styled(SectionCardRow)`
  flex: 1;
  align-self: baseline;
`;

const Grid = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  width: 100%;
`;

const StyledPasswordFormItem = styled(FormItem)`
  flex: 1;
  max-width: 180px;
  align-self: center;
`;

const DotWrapper = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Export helper functions
export { isFormValidUtil as isFormValid };

// Export component and its hooks
export const usePaymentMethodForm = () => {
  return { getCardNumber };
};

// Export types for external use
export type { PaymentFormValues, RegisterType, CardType, PaymentMethodFormRef };
